<script setup lang="ts">
import { storeToRefs } from 'pinia';

import { AppLogo } from '@clovyr/bed';
import { formatWNextPrice, usePrice } from '@clovyr/bed/composables/usePrice';
import type { Manifest } from '@clovyr/pollen/manifest';
import type { SalesPricingContext } from '@clovyr/pollen/subscription';

import { usePayments } from '@/composables/usePayments';
import { useEventBus } from '@/stores/event_bus';
import { useLauncherStore } from '@/stores/launcher_store';
import { usePollenStore } from '@/stores/pollen_store';

const {
  app,
  panel = false,
  buttons = false,
} = defineProps<{ app: Manifest; panel?: boolean; buttons?: boolean }>();

const emit = defineEmits(['close']);

const { isUserAuthenticated } = storeToRefs(usePollenStore());
const { hasPaymentOnFile, captureLaunchPayment } = usePayments();
const { eventBus } = useEventBus();
const launcherStore = useLauncherStore();
const {
  manifest: launcherManifest,
  wnextAppPrice,
  billingPeriod,
  billingPeriodLabel,
  salesTier,
  salesToken,
  showBillingPeriodStep,
} = storeToRefs(launcherStore);

const appRef = computed(() => app);
const { wnextAppPrice: appPrice } = usePrice(appRef);
const isCurrentLauncherApp = computed(
  () => launcherManifest.value?.metadata?.id === app.metadata.id,
);
const isAiCloud = computed(() => app.metadata.id === 'clovyr-ai-cloud');
const activePrice = computed(() =>
  isCurrentLauncherApp.value ? wnextAppPrice.value : appPrice.value,
);
const formattedActivePrice = computed(() => formatWNextPrice(activePrice.value));
const activePriceSuffix = computed(() => {
  if (isAiCloud.value && isCurrentLauncherApp.value) {
    return billingPeriodLabel.value === 'Monthly' ? 'month' : 'year';
  }

  return 'month';
});
const launchSalesPricingContext = computed<SalesPricingContext | undefined>(() => {
  if (!(isAiCloud.value && isCurrentLauncherApp.value && showBillingPeriodStep.value)) {
    return undefined;
  }

  return {
    salesToken: salesToken.value,
    salesTier: salesTier.value,
    billingPeriod: billingPeriod.value,
  };
});

const onClickContinue = async () => {
  if (isUserAuthenticated.value) {
    if (hasPaymentOnFile.value) {
      // if the user is authenticated and has a payment method on file, we can launch the app
      eventBus.emit('launch:continue');
      emit('close');
    } else {
      await captureLaunchPayment(app.metadata.id, launchSalesPricingContext.value);
    }
  }
};
</script>

<template>
  <div :class="{ panel }" class="marketing">
    <template v-if="panel">
      <div class="heading">Almost there...</div>
      <div>
        <AppLogo class="app-logo-detail" size="small" :logo="app.metadata">
          <template #title>
            <div class="app-launcher-title">{{ app.metadata.name }}</div>
          </template>
        </AppLogo>
      </div>
    </template>
    <template v-else>
      <div class="upgrade-heading">
        <h2>Launch</h2>
        <AppLogo class="app-logo-detail" size="x-small" :logo="app.metadata">
          <template #title>
            <div class="app-launcher-title">{{ app.metadata.name }}</div>
          </template>
        </AppLogo>
      </div>
    </template>

    <p v-if="!isUserAuthenticated">
      To use <strong>{{ app.metadata.name }}</strong
      >, you'll need to sign up or log in first.
    </p>
    <p v-else-if="!hasPaymentOnFile">
      To use <strong>{{ app.metadata.name }}</strong
      >, you'll need to add a payment method.
    </p>

    <p>
      After a <strong>7-day free trial</strong>, your subscription will continue at
      <strong>${{ formattedActivePrice }}/{{ activePriceSuffix }}</strong> unless canceled.
    </p>

    <div v-if="buttons" class="row">
      <div class="grid__col-md-3">
        <Button label="cancel" @click="$emit('close')"></Button>
      </div>
      <div class="grid__col-md-9">
        <Button
          :disabled="!isUserAuthenticated"
          label="continue"
          reversed
          @click="onClickContinue"
        ></Button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use 'sass:color';
@use '@clovyr/bed/styles/vendor/bootstrap/scss/bootstrap';
@use '@clovyr/bed/styles/core';
@use 'src/styles/core/page';
@use 'src/styles/core/vertical-spacing';
@use 'styles/core/util';
@use 'styles/vendor/bootstrap/scss/mixins/breakpoints';

.panel.marketing {
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

@media (min-width: 768px) and (max-width: 875px) {
  .panel.marketing {
    max-width: 100% !important;
    width: 100% !important;
  }
}

.panel {
  background: linear-gradient(180deg, rgb(65 59 79 / 50%) 0%, #413b4f 91.78%);
  border-radius: var(--module-border-radius);
  text-align: left;
  padding: 10px 24px 20px 24px;

  @supports (backdrop-filter: blur()) {
    backdrop-filter: blur(util.rem(18));
    background: linear-gradient(
      180deg,
      rgb(65 59 79 / 40%) 0%,
      color.adjust(#413b4f, $alpha: -0.4) 91.78%
    );
  }
}

.heading {
  text-align: left;
  font-size: 1.5rem;
  font-weight: 500;
}

.upgrade-heading {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;

  @include breakpoints.media-breakpoint-up(sm) {
    margin-top: 1rem;
  }

  h2 {
    color: #ffffff;
    margin-right: 1rem;
  }
}

.app-logo-detail {
  display: flex;
  justify-content: left;
  align-items: center;
}

.app-launcher-title {
  font-size: 18px;
  font-weight: 600;
}

.row {
  display: flex;
  justify-content: center;
}

.grid__col-md-3,
.grid__col-md-9 {
  flex: none;
  width: auto;
}
</style>
